/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { useEffect, useCallback, useRef, useState } from 'react';
import useSWR, { useSWRInfinite } from 'swr';
import pickBy from 'lodash/pickBy';

import { fetchGet, fetchPut } from 'FetchAPIs';

/**
 * This hook is like useEffect, but will actually wait on the the callback to finish
 * @param {function} callback call back function to await on
 * @param {array} effectDependencies, optional array of dependencies to pass to useEffect
 * @return {bool} true or false state variable reporting that the callback has finished
 */
export const useAwaitEffect = (callback, effectDependencies = []) => {
  const [hasCallbackFinished, setHasCallbackFinished] = useState(false);
  const isMounted = useRef(true);

  // Setup async function waiting on the callback
  const asyncCallback = useCallback(
    async () => {
      await callback();
      // This prevents us from calling the setState if the component is no longer mounted (memory leak)
      if (isMounted?.current) {
        setHasCallbackFinished(true);
      }
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // actually call the callback with useEffect
  useEffect(
    () => {
      // The response from the callback will be the 'cleanup' function for useEffect
      const cleanupFunction = asyncCallback();
      return () => {
        if (cleanupFunction === 'function') {
          // only functions are supported as effect responses
          cleanupFunction();
        }
        if (isMounted) {
          // mark the component unmounted so that we prevent the stateful callback
          isMounted.current = false;
        }
      };
    },
    effectDependencies // eslint-disable-line react-hooks/exhaustive-deps
  );
  return [hasCallbackFinished];
};

// React hook for delaying calls with time
// returns callback to use for cancelling
// for more info, https://medium.com/javascript-in-plain-english/usetimeout-react-hook-3cc58b94af1f
export const useTimeout = (
  callback, // function to call. No args passed.
  // if you create a new callback each render, then previous callback will be cancelled on render.
  timeout = 0 // delay, ms (default: immediately put into JS Event Queue)
) => {
  const timeoutIdRef = useRef();
  const cancel = useCallback(() => {
    const timeoutId = timeoutIdRef.current;
    if (timeoutId) {
      timeoutIdRef.current = undefined;
      clearTimeout(timeoutId);
    }
  }, [timeoutIdRef]);

  // this invokes the set timeout
  const cachedSetTimeout = useCallback(() => {
    if (!timeoutIdRef.current) {
      // only trigger the timeout if it's not already running
      // eslint-disable-next-line @typescript-eslint/no-implied-eval
      timeoutIdRef.current = setTimeout(callback, timeout);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // This will auto cancel the timeout if the component is unmounted
  useEffect(
    () => cancel,
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return cachedSetTimeout;
};

/**
 * SWR pagination implementation will format the fetch functions according to our practices
 * (we do a lot of formatting in our API calls)
 * @param {function} getKey must return a string URL
 * @param {function} fetchFunction monitor-specific fetch function from FetchAPIs (i.e. fetchPost)
 * @param {object} queryParams
 * @param {object} options
 * @param {object} body
 * @param {string} dataName name of the returned data key
 * @param {object} swrOptions
 * @returns {object} { [dataName], isLoading, isError, size, setSize }
 */
export const useCustomSWRInfinite = (
  getKey,
  fetchFunction,
  queryParams = {},
  options = {},
  body = {},
  dataName = 'data',
  swrOptions = { initialSize: 3, refreshInterval: 120000, revalidateOnFocus: false }
) => {
  // parse out the query params so that we can use our API call implementation
  const parseFetcherSWR = fullUrl => {
    const [url, queryParamsFromUrl] = fullUrl.split('?');
    const searchParams = new URLSearchParams(queryParamsFromUrl);
    const searchValues = Object.keys(queryParams).reduce(
      (values, searchParamsKey) => ({
        ...values,
        [searchParamsKey]: searchParams.get(searchParamsKey),
      }),
      {}
    );

    return fetchFunction(url, body, options, searchValues);
  };

  const { data, error, size, setSize } = useSWRInfinite(getKey, parseFetcherSWR, swrOptions);

  return {
    [dataName]: data,
    isLoading: !error && !data,
    error,
    size,
    setSize,
  };
};

/**
 * Standard SWR implentation that formats the fetch functions according to our practices
 * (we do a lot of formatting in our API calls)
 * @param {object} { url, fetchFunction, fetchMethod, queryParams, options, body, dataName, swrOptions }
 * url: URL to fetch to
 * fetchFunction: monitor-specific fetch function from FetchAPIs (i.e. fetchPost)
 * fetchMethod: the method to fetch by is needed because we pass the arguments differently
 * queryParams:
 * options:
 * body:
 * dataName: name of the returned data key
 * swrOptions:
 * @returns {object} { [dataName], isLoading, isError, size, setSize }
 */
export const useCustomSWR = ({
  url,
  fetchFunction,
  queryParams = {},
  options = {},
  body = {},
  dataName = 'data',
  swrOptions = {},
  fetchMethod = 'POST',
}) => {
  // parse out the query params so that we can use our API call implementation
  const parseFetcherSWR = fullUrl => {
    const [baseUrl, queryParamsFromUrl] = fullUrl.split('?');
    const searchParams = new URLSearchParams(queryParamsFromUrl);
    const searchValues = Object.keys(queryParams).reduce(
      (values, searchParamsKey) => ({
        ...values,
        [searchParamsKey]: searchParams.get(searchParamsKey),
      }),
      {}
    );

    // Unfortunately our fetch convention is different across fetch methods
    return fetchMethod === 'POST'
      ? fetchFunction(baseUrl, body, options, searchValues)
      : fetchFunction(baseUrl, options, searchValues);
  };

  const { data, error } = useSWR(url, parseFetcherSWR, swrOptions);

  return {
    [dataName]: data,
    isLoading: !error && !data,
    error,
  };
};

/**
 * Standard SWR implentation that formats the fetch functions according to our practices
 * Used for APIs that search should be sent in the body.
 * @param {object} { url, fetchFunction, fetchMethod, queryParams, options, body, dataName, swrOptions }
 * url: URL to fetch to
 * fetchFunction: monitor-specific fetch function from FetchAPIs (i.e. fetchPost)
 * queryParams:
 * options:
 * body:
 * dataName: name of the returned data key
 * swrOptions:
 * @returns {object} { [dataName], isLoading, error, }
 */
export const useCustomSWRSearch = ({
  url,
  fetchFunction,
  queryParams = {},
  options = {},
  body = {},
  dataName = 'data',
  swrOptions = {},
}) => {
  // parse out the query params so that we can use our API call implementation
  const parseFetcherSWR = fullUrl => {
    const [baseUrl, queryParamsFromUrl] = fullUrl.split('?');
    const searchParams = new URLSearchParams(queryParamsFromUrl);
    const searchValues = Object.keys(queryParams).reduce(
      (values, searchParamsKey) => ({
        ...values,
        [searchParamsKey]: searchParams.get(searchParamsKey),
      }),
      {}
    );

    return fetchFunction(
      baseUrl,
      // force the search from the url searchInput query param
      {
        search: searchParams.get('searchInput'),
        filter: {
          ...pickBy(
            JSON.parse(searchParams.get('filterInput') ?? '{}'),
            val => val && val.length > 0
          ),
        },
        ...body,
      },
      options,
      searchValues
    );
  };

  const { data, error, isValidating, mutate } = useSWR(url, parseFetcherSWR, swrOptions);

  return {
    [dataName]: data,
    isLoading: isValidating,
    error,
    mutate,
  };
};

/**
 * Wraps fetches to ease the use of table view APIs
 * @param {string} tableId the table id within the tableViews that will be used i.e. 'alert'
 * @param {string} dataName
 * {
 * alert: {
 * columns: []
 * views: [{
 *    id: blob,
 *    ...
 * }]}
 */
export const useTableViews = (tableId, dataName = 'data', userEmailId) => {
  const VIEW_API_URL = '/api/hierarchies/tenant/metadata';

  const { data, error, mutate } = useSWR(VIEW_API_URL, fetchGet(VIEW_API_URL, { userEmailId }, {}));
  /**
   *
   * @param {Array<Object>} updatedTableViews
   */
  const updateTableViews = async updatedTableViews => {
    const updatedViews = { ...data, [tableId]: updatedTableViews };
    // first update the local state without revalidation
    mutate(updatedViews, false);
    // then update the API
    await fetchPut(VIEW_API_URL, updatedViews, { userEmailId }, {});
    // then force SWR to revalidate
    mutate();
  };

  return {
    [dataName]: data?.[tableId],
    isLoading: !error && !data,
    error,
    updateTableViews,
  };
};

/**
 * This hook stores the previous value until it changes
 * @param {*} value
 * @returns
 */
export const usePrevious = value => {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef();
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
};
